/**
 * Paid-ad touches for the Revenue API lead intake (E12 P3, #586).
 *
 * The fields and rules are the P2 attribution contract, section A2
 * (`docs/contracts/paid-growth-attribution.md` in topgeo-revenue-os): a first
 * and a last touch, each with its time, UTMs (including `utm_id`), landing URL,
 * referrer and click IDs, plus the visitor's ad consent and a browser-made
 * `client_event_id` that the browser "Lead" event shares with the server's
 * conversion upload so the platform counts it once.
 *
 * Everything here is off until `NEXT_PUBLIC_AD_ATTRIBUTION_ENABLED` is "true".
 * The intake refuses fields it does not know, so switching this on before P2
 * is deployed would make it refuse every lead. Off, the site sends exactly what
 * it sent before.
 *
 * Consent decides what is kept, not the server alone:
 *   - no optional consent: nothing is stored;
 *   - analytics or marketing: UTMs, landing URL and referrer are stored;
 *   - marketing only: click IDs and the Meta `_fbc`/`_fbp` cookies as well.
 * Withdrawing marketing consent removes the stored click IDs; withdrawing both
 * removes the record (see `CookieConsent.tsx`).
 *
 * `library/first-touch.ts` is separate on purpose: it feeds the audit backend's
 * own whitelist, which does not accept these keys.
 */
import { readConsent } from "./cookie-consent";

export const AD_TOUCH_KEY = "topgeo_ad_touch_v1";
/** Proposed retention. The founder confirms it (P2 contract A10, decision 1). */
export const AD_TOUCH_MAX_AGE_DAYS = 90;

const MAX_TEXT = 200;
const MAX_URL = 2048;
const MAX_STORAGE_BYTES = 8192;
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_id"] as const;
const CLICK_KEYS = ["gclid", "gbraid", "wbraid", "fbclid"] as const;
const ID_KEYS = [...CLICK_KEYS, "fbc", "fbp"] as const;
// The patterns P2 enforces. A value that fails is not sent at all.
const CLICK_ID = /^[A-Za-z0-9_-]{1,512}$/;
const FBC = /^fb\.\d\.\d{10,13}\.[A-Za-z0-9_-]+$/;
const FBP = /^fb\.\d\.\d{10,13}\.\d+$/;

type UtmKey = (typeof UTM_KEYS)[number];
type IdKey = (typeof ID_KEYS)[number];
export type AdTouch = { timestamp: string; landing_url?: string; referrer?: string } & Partial<Record<UtmKey | IdKey, string>>;
export type AdConsent = "granted" | "denied" | "unknown";
type Stored = { first: AdTouch; last: AdTouch };

/** Read literally so Next inlines it at build time. */
export function adAttributionEnabled(): boolean {
  return process.env.NEXT_PUBLIC_AD_ATTRIBUTION_ENABLED === "true";
}

/** No consent record yet is `unknown`; a recorded refusal of marketing is `denied`. */
export function adConsent(): AdConsent {
  const consent = readConsent();
  if (!consent) return "unknown";
  return consent.marketing ? "granted" : "denied";
}

function text(value: unknown, max = MAX_TEXT): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > max || /[\u0000-\u001f\u007f]/.test(trimmed)) return undefined;
  return trimmed;
}

function validId(key: IdKey, value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  if (key === "fbc") return value.length <= 600 && FBC.test(value) ? value : undefined;
  if (key === "fbp") return value.length <= 100 && FBP.test(value) ? value : undefined;
  return CLICK_ID.test(value) ? value : undefined;
}

/** The landing URL keeps its UTMs only; click IDs never travel inside a URL. */
function landingUrl(href: string): string | undefined {
  try {
    const url = new URL(href);
    const params = new URLSearchParams();
    for (const key of UTM_KEYS) {
      const value = text(url.searchParams.get(key));
      if (value) params.set(key, value);
    }
    url.search = params.toString();
    url.hash = "";
    const out = url.toString();
    return out.length <= MAX_URL ? out : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The page URL the intake already receives, without click IDs: those travel
 * only in a touch, and only with marketing consent.
 */
export function pageUrlWithoutClickIds(href: string): string {
  try {
    const url = new URL(href);
    for (const key of CLICK_KEYS) url.searchParams.delete(key);
    return url.toString();
  } catch {
    return "";
  }
}

/** An external referrer, without its query: a query can carry anything. */
function referrerUrl(referrer: string, host: string): string | undefined {
  try {
    const url = new URL(referrer);
    if (!url.hostname || url.host === host || (url.protocol !== "https:" && url.protocol !== "http:")) return undefined;
    const out = url.origin + url.pathname;
    return out.length <= MAX_URL ? out : undefined;
  } catch {
    return undefined;
  }
}

function cookie(name: string): string | undefined {
  try {
    const found = document.cookie.split(";").map((c) => c.trim()).find((c) => c.startsWith(name + "="));
    return found ? decodeURIComponent(found.slice(name.length + 1)) : undefined;
  } catch {
    return undefined;
  }
}

/** Meta's cookies, read at the moment they are needed: the Pixel sets them late. */
function metaIds(touch: AdTouch, now: Date): Pick<AdTouch, "fbc" | "fbp"> {
  const out: Pick<AdTouch, "fbc" | "fbp"> = {};
  const fbc = validId("fbc", cookie("_fbc")) ?? touch.fbc;
  // No `_fbc` cookie yet (Pixel blocked or not loaded): build it the way Meta
  // documents it, from the fbclid of this touch.
  const built = touch.fbclid ? validId("fbc", `fb.1.${Date.parse(touch.timestamp) || now.getTime()}.${touch.fbclid}`) : undefined;
  if (fbc ?? built) out.fbc = fbc ?? built;
  const fbp = validId("fbp", cookie("_fbp")) ?? touch.fbp;
  if (fbp) out.fbp = fbp;
  return out;
}

function hasAdEvidence(touch: AdTouch): boolean {
  return UTM_KEYS.some((k) => touch[k]) || CLICK_KEYS.some((k) => touch[k]);
}

function withoutIds(touch: AdTouch): AdTouch {
  const copy = { ...touch };
  for (const key of ID_KEYS) delete copy[key];
  return copy;
}

/** This page, as a touch. Click IDs only when marketing consent allows them. */
function currentTouch(now: Date, marketing: boolean): AdTouch {
  const touch: AdTouch = { timestamp: now.toISOString() };
  const location = window.location;
  const params = new URLSearchParams(location.search);
  for (const key of UTM_KEYS) {
    const value = text(params.get(key));
    if (value) touch[key] = value;
  }
  const landing = landingUrl(location.href);
  if (landing) touch.landing_url = landing;
  const referrer = typeof document === "undefined" ? undefined : referrerUrl(document.referrer, location.host);
  if (referrer) touch.referrer = referrer;
  if (marketing) {
    for (const key of CLICK_KEYS) {
      const value = validId(key, params.get(key));
      if (value) touch[key] = value;
    }
    Object.assign(touch, metaIds(touch, now));
  }
  return touch;
}

/** A stored touch is untrusted input: rebuild it field by field. */
function rebuild(value: unknown): AdTouch | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  if (typeof raw.timestamp !== "string" || !Number.isFinite(Date.parse(raw.timestamp))) return null;
  const touch: AdTouch = { timestamp: raw.timestamp };
  for (const key of UTM_KEYS) {
    const v = text(raw[key]);
    if (v) touch[key] = v;
  }
  for (const key of ["landing_url", "referrer"] as const) {
    const v = text(raw[key], MAX_URL);
    if (v) touch[key] = v;
  }
  for (const key of ID_KEYS) {
    const v = validId(key, raw[key]);
    if (v) touch[key] = v;
  }
  return touch;
}

function readStored(now: Date, marketing: boolean): Stored | null {
  try {
    const raw = window.localStorage.getItem(AD_TOUCH_KEY);
    if (!raw || raw.length > MAX_STORAGE_BYTES) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const first = rebuild(parsed.first);
    const last = rebuild(parsed.last);
    if (!first || !last) return null;
    if (now.getTime() - Date.parse(first.timestamp) > AD_TOUCH_MAX_AGE_DAYS * 86_400_000) {
      window.localStorage.removeItem(AD_TOUCH_KEY);
      return null;
    }
    return marketing ? { first, last } : { first: withoutIds(first), last: withoutIds(last) };
  } catch {
    return null;
  }
}

function writeStored(value: Stored): void {
  try {
    const encoded = JSON.stringify(value);
    if (encoded.length <= MAX_STORAGE_BYTES) window.localStorage.setItem(AD_TOUCH_KEY, encoded);
  } catch {
    /* Storage blockers fail open. */
  }
}

/**
 * Record this page view. The first touch is kept until it expires; the last
 * touch moves only when a page arrives with ad evidence (a UTM or a click ID),
 * so clicking around the site does not erase the ad that brought the visitor.
 */
export function captureAdTouch(now = new Date()): void {
  if (!adAttributionEnabled() || typeof window === "undefined") return;
  const consent = readConsent();
  if (!consent || (!consent.analytics && !consent.marketing)) return;
  try {
    const observed = currentTouch(now, consent.marketing);
    const stored = readStored(now, consent.marketing);
    if (!stored) {
      writeStored({ first: observed, last: observed });
      return;
    }
    writeStored({ first: stored.first, last: hasAdEvidence(observed) ? observed : stored.last });
  } catch {
    /* Never let attribution break a page. */
  }
}

/** Withdrawal: click IDs go with marketing consent; everything goes with both. */
export function forgetAdTouch(keepUtms: boolean): void {
  try {
    if (!keepUtms) {
      window.localStorage.removeItem(AD_TOUCH_KEY);
      return;
    }
    const stored = readStored(new Date(), false);
    if (stored) writeStored(stored);
    else window.localStorage.removeItem(AD_TOUCH_KEY);
  } catch {
    /* Storage unavailable. */
  }
}

export type AdIntakeFields = {
  ad_consent: AdConsent;
  first_touch?: AdTouch;
  last_touch?: AdTouch;
};

/**
 * The A2 fields for one intake call. Without any consent only `ad_consent`
 * is sent, and the intake falls back to the flat UTMs of the current page as
 * it always has. Storage blocked: the current page stands in for both touches.
 */
export function adIntakeFields(now = new Date()): AdIntakeFields {
  const ad_consent = adConsent();
  if (typeof window === "undefined") return { ad_consent };
  const consent = readConsent();
  if (!consent || (!consent.analytics && !consent.marketing)) return { ad_consent };
  const marketing = consent.marketing;
  const stored = readStored(now, marketing);
  const observed = currentTouch(now, marketing);
  const first = stored?.first ?? observed;
  let last = stored ? (hasAdEvidence(observed) ? observed : stored.last) : observed;
  // `_fbp`/`_fbc` may have appeared since the touch was stored.
  if (marketing) last = { ...last, ...metaIds(last, now) };
  return { ad_consent, first_touch: first, last_touch: last };
}

// --- client_event_id --------------------------------------------------------

const pendingEventIds = new Map<string, string>();

function randomId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID().replace(/-/g, "");
  } catch {
    /* Fall through. */
  }
  return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join("");
}

/**
 * One id per submission. A retry or a double click of the same form reuses it,
 * so the platform sees one lead; it is released once the lead is stored.
 */
export function clientEventIdFor(form: string): string {
  const existing = pendingEventIds.get(form);
  if (existing) return existing;
  const id = "tgce_" + randomId();
  pendingEventIds.set(form, id);
  return id;
}

export function releaseClientEventId(form: string): void {
  pendingEventIds.delete(form);
}
